/** English texts of the car designer. */
export const enCar = {
  sections: {
    car: {
      model: 'Model',
      engine: 'Engine',
      trim: 'Trim',
      drivetrain: 'Drivetrain',
      suspension: 'Suspension',
      wheels: 'Wheels',
      brakes: 'Brakes',
      aero: 'Aerodynamics',
      paint: 'Paint',
    },
  },
  params: {
    car: {
      shape: 'Body style',
      length: 'Length',
      width: 'Width',
      height: 'Height',
      wheelbase: 'Wheelbase',
      overhangFront: 'Front overhang',
      trackFront: 'Front track (share of width)',
      trackRear: 'Rear track (share of width)',
      noseHeight: 'Nose height',
      hoodHeight: 'Bonnet height',
      windshieldRake: 'Windscreen rake',
      roofLength: 'Roof length',
      rearSlope: 'Rear slope (fastback)',
      deckHeight: 'Boot lid height',
      beltline: 'Beltline',
      tumblehome: 'Tumblehome',
      frontTaper: 'Front taper',
      rearTaper: 'Rear taper',
      archFlare: 'Arch flare',
      chassis: 'Chassis type',
      chassisMaterial: 'Chassis material',
      frontSuspension: 'Front suspension',
      rearSuspension: 'Rear suspension',
      quality: 'Quality',
      placement: 'Engine placement',
      bodyMaterial: 'Body material',
      interior: 'Interior',
      seats: 'Seats',
      entertainment: 'Entertainment',
      safety: 'Safety equipment',
      powerSteering: 'Power steering',
      abs: 'ABS',
      tcs: 'Traction control',
      esc: 'Stability control (ESC)',
      launchControl: 'Launch control',
      layout: 'Drive layout',
      awdFrontShare: 'Front torque share',
      gearbox: 'Gearbox',
      gears: 'Gears',
      ratio: 'Gear',
      cvtLow: 'Lowest ratio',
      cvtHigh: 'Highest ratio',
      finalDrive: 'Final drive',
      differential: 'Differential',
      springs: 'Springs',
      dampers: 'Dampers',
      antiRoll: 'Anti-roll bars',
      rideHeight: 'Ride height',
      springStiffness: 'Spring stiffness',
      damping: 'Damping',
      antiRollBar: 'Anti-roll bar',
      camber: 'Camber',
      tireType: 'Tyre type',
      rimDiameter: 'Rim diameter',
      rimMaterial: 'Rim material',
      rimDesign: 'Rim design',
      aspectRatio: 'Aspect ratio (sidewall)',
      pads: 'Brake pads',
      bias: 'Front brake bias',
      type: 'Type',
      diameter: 'Diameter',
      pistons: 'Pistons per caliper',
      frontDownforce: 'Front downforce (splitter)',
      rearDownforce: 'Rear downforce (wing)',
      undertray: 'Undertray',
      cooling: 'Cooling openings',
      color: 'Colour',
      finish: 'Finish',
      flake: 'Flake',
      pearlColor: 'Colour flop',
      clearcoat: 'Clear coat',
    },
  },
  options: {
    bodyMaterial: {
      steel: 'Steel',
      'galvanised-steel': 'Galvanised steel',
      aluminium: 'Aluminium',
      fibreglass: 'Fibreglass',
      cfrp: 'Carbon fibre',
    },
    suspensionType: {
      'solid-axle': 'Solid axle',
      macpherson: 'MacPherson',
      'double-wishbone': 'Double wishbone',
      pushrod: 'Pushrod',
      'torsion-beam': 'Torsion beam',
      'semi-trailing': 'Semi-trailing arm',
      'multi-link': 'Multi-link',
    },
    paintZone: { roof: 'Contrast roof', hood: 'Contrast bonnet', lower: 'Contrast sills/bumpers' },
    params: {
      car: {
        shape: {
          coupe: 'Coupé',
          sedan: 'Sedan',
          wagon: 'Estate',
          hatchback: 'Hatchback',
          suv: 'SUV',
          roadster: 'Roadster',
          pickup: 'Pickup',
          van: 'Van',
        },
        chassis: { monocoque: 'Monocoque', ladder: 'Ladder frame', spaceframe: 'Spaceframe' },
        placement: {
          'front-longitudinal': 'Front longitudinal',
          'front-transverse': 'Front transverse',
          'mid-longitudinal': 'Mid longitudinal',
          'mid-transverse': 'Mid transverse',
          'rear-longitudinal': 'Rear',
        },
        interior: {
          basic: 'Basic',
          standard: 'Standard',
          premium: 'Premium',
          luxury: 'Luxury',
          sport: 'Sport',
          race: 'Race',
        },
        entertainment: {
          none: 'None',
          radio: 'Radio',
          infotainment: 'Infotainment',
          'premium-infotainment': 'Premium infotainment',
        },
        safety: { basic: 'Basic', standard: 'Standard', advanced: 'Advanced', modern: 'Modern' },
        powerSteering: { none: 'None', hydraulic: 'Hydraulic', electric: 'Electric' },
        layout: { fwd: 'Front-wheel drive', rwd: 'Rear-wheel drive', awd: 'All-wheel drive' },
        gearbox: {
          manual: 'Manual',
          automatic: 'Automatic',
          dct: 'Dual-clutch',
          sequential: 'Sequential',
          cvt: 'CVT',
        },
        differential: {
          open: 'Open',
          viscous: 'Viscous LSD',
          'plate-lsd': 'Plate LSD',
          torsen: 'Torsen',
          active: 'Active (torque vectoring)',
        },
        springs: {
          passive: 'Steel springs',
          progressive: 'Progressive',
          hydropneumatic: 'Hydropneumatic',
          air: 'Air suspension',
        },
        dampers: { 'twin-tube': 'Twin-tube', 'mono-tube': 'Mono-tube', adaptive: 'Adaptive' },
        antiRoll: { none: 'None', passive: 'Passive', active: 'Active' },
        tireType: {
          eco: 'Eco',
          touring: 'Touring',
          sport: 'Sport',
          'semi-slick': 'Semi-slick',
          slick: 'Slick',
          'all-terrain': 'All-terrain',
          offroad: 'Off-road',
        },
        rimMaterial: {
          steel: 'Steel',
          alloy: 'Cast alloy',
          forged: 'Forged alloy',
          magnesium: 'Magnesium',
          cfrp: 'Carbon fibre',
        },
        rimDesign: {
          steel: 'Steel wheel',
          'five-spoke': 'Five-spoke',
          'multi-spoke': 'Multi-spoke',
          mesh: 'Mesh',
          aero: 'Aero disc',
        },
        pads: {
          organic: 'Organic',
          'semi-metallic': 'Semi-metallic',
          ceramic: 'Ceramic',
          race: 'Race',
        },
        type: {
          drum: 'Drum',
          'solid-disc': 'Solid disc',
          'vented-disc': 'Vented disc',
          'carbon-ceramic': 'Carbon-ceramic',
        },
        undertray: { none: 'None', partial: 'Partial', full: 'Full' },
        finish: {
          solid: 'Solid',
          metallic: 'Metallic',
          pearl: 'Pearl',
          matte: 'Matte',
          satin: 'Satin',
          chrome: 'Chrome',
        },
      },
    },
  },
  help: {
    params: {
      car: {
        shape: 'Basic body style. Sets roof line, rear end and base drag coefficient.',
        wheelbase: 'Distance between the axles. Long = stable and roomy, short = agile.',
        chassis:
          'Monocoque is light and stiff, a ladder frame is tough and heavy (utility, off-road), a spaceframe is light but expensive.',
        placement: 'A mid engine balances the weight, front transverse saves space.',
        bodyMaterial: 'Aluminium and carbon save a lot of weight but cost much more.',
        layout:
          'Rear and all-wheel drive put down more power when accelerating; front drive is light and cheap.',
        gearbox:
          'Dual-clutch shifts fastest, automatic is most comfortable, manual is light and cheap.',
        finalDrive: 'A higher final drive accelerates harder but lowers top speed.',
        differential:
          'Limited-slip differentials split torque better between both wheels: more traction.',
        rideHeight:
          'Lower drops the centre of gravity and drag, higher adds comfort and off-road ability.',
        springStiffness: 'Stiff springs improve handling, soft ones comfort.',
        camber: 'Slight negative camber (≈ −1.5°) improves cornering grip.',
        tireType: 'Softer compounds grip better but roll harder and wear faster.',
        bias: 'Share of braking force at the front. Ideally a bit higher than the weight distribution because braking shifts load forward.',
        rearDownforce: 'Rear wings add grip in fast corners but cost top speed.',
        cooling: 'More cooling air prevents powerful engines from overheating but adds drag.',
        quality: 'Higher quality improves reliability and prestige but costs more.',
      },
    },
  },
  formula: {
    params: {
      car: {
        finalDrive: 'F_wheel = T · i_g · i_f · η / r',
        rideHeight: 'F_res = ½ ρ c_d A v² + f_r (1 + V/160) (m g + F_down)',
        bias: 'Optimum ≈ front axle load + μ · h_cg / L',
      },
    },
  },
  stats: {
    car: {
      power: 'Power',
      mass: 'Kerb weight',
      accel: 'Acceleration',
      topSpeed: 'Top speed',
      braking: 'Braking distance',
      lateral: 'Lateral grip',
      consumption: 'Fuel economy',
      cost: 'Production cost',
      running: 'Running {value}/year',
      powerToMass: 'Power-to-weight',
      drag: 'Cd · A',
      quarterMile: 'Quarter mile',
      elasticity: 'In-gear',
      range: 'Range',
    },
  },
  charts: {
    traction: 'Tractive force per gear and road load (kN)',
    resistance: 'Road load',
  },
  ratings: {
    car: {
      drivability: 'Drivability',
      sportiness: 'Sportiness',
      comfort: 'Comfort',
      safety: 'Safety',
      practicality: 'Practicality',
      utility: 'Utility',
      offroad: 'Off-road',
      reliability: 'Reliability',
      prestige: 'Prestige',
    },
  },
  warnings: {
    car: {
      engineTooLong: {
        text: 'The engine is too long for the engine bay.',
        fix: 'Lengthen the front overhang, pick a shorter engine or change the placement.',
      },
      engineTooWide: {
        text: 'The engine is too wide for the engine bay.',
        fix: 'Widen the car, use narrower tyres or an inline engine.',
      },
      engineTooLongTransverse: {
        text: 'Mounted transversely the engine does not fit between the wheels.',
        fix: 'Mount it longitudinally, widen the car or use fewer cylinders.',
      },
      engineNoRoomMid: {
        text: 'Not enough room behind the seats for the engine.',
        fix: 'Lengthen the wheelbase or mount the engine at the front.',
      },
      engineTooTall: {
        text: 'The engine sticks out of the bonnet.',
        fix: 'Raise the bonnet, lower the ride height or use a flatter engine.',
      },
      roadsterSeats: {
        text: 'A roadster has at most two seats.',
        fix: 'Reduce the seats or choose another body style.',
      },
      tooManySeats: {
        text: 'That many seats only fit in a van, SUV or estate.',
        fix: 'Change the body style or reduce the seats.',
      },
      gearboxTorque: {
        text: 'The gearbox cannot handle the torque.',
        fix: 'Choose a sequential or automatic gearbox, raise quality or detune the engine.',
      },
      firstGearTall: {
        text: 'First gear is too tall to pull away.',
        fix: 'Shorten first gear or the final drive.',
      },
      topGearShort: {
        text: 'Top speed is limited by the rev limiter.',
        fix: 'Use a longer final drive or top gear.',
      },
      fwdTooMuchPower: {
        text: 'A lot of power for front-wheel drive.',
        fix: 'All- or rear-wheel drive, a limited-slip differential or wider tyres help.',
      },
      noTraction: {
        text: 'Very high power without traction control.',
        fix: 'Enable traction control or choose all-wheel drive.',
      },
      brakesWeak: {
        text: 'The brakes are too weak.',
        fix: 'Fit larger discs, more pistons or better pads.',
      },
      overheating: {
        text: 'The engine is at risk of overheating.',
        fix: 'Enlarge the cooling openings.',
      },
      brakeRimClearance: {
        text: 'The brake disc does not fit inside the rim.',
        fix: 'Choose larger rims or smaller discs.',
      },
      tireTooWide: {
        text: 'The tyres are too wide for the wheel arches.',
        fix: 'Add arch flare or choose narrower tyres.',
      },
      weightDistribution: {
        text: 'Unusual weight distribution.',
        fix: 'Change the engine placement or adjust the wheelbase.',
      },
      understeer: {
        text: 'The car understeers heavily.',
        fix: 'Soften the front anti-roll bar, stiffen the rear or fit wider front tyres.',
      },
      oversteer: {
        text: 'The car oversteers heavily.',
        fix: 'Soften the rear anti-roll bar, stiffen the front or fit wider rear tyres.',
      },
      cabinIntrusion: {
        text: 'The engine intrudes into the cabin.',
        fix: 'Lengthen the wheelbase or move the engine forward.',
      },
    },
  },
  massGroups: {
    chassis: 'Body in white',
    body: 'Panels',
    glass: 'Glazing',
    engine: 'Engine',
    drivetrain: 'Drivetrain',
    suspension: 'Suspension',
    wheels: 'Wheels',
    brakes: 'Brakes',
    interior: 'Interior',
    equipment: 'Equipment',
    fuel: 'Fuel',
    exhaust: 'Exhaust',
    cooling: 'Cooling',
    electrical: 'Electrics',
    aero: 'Aero parts',
    misc: 'Other',
  },
  designer: {
    view: 'View',
    views: { normal: 'Normal', xray: 'X-ray', exploded: 'Exploded' },
    shape: 'Shape',
    chassis: 'Chassis',
    front: 'Front',
    rear: 'Rear',
    zones: 'Multi-colour',
    wrap: 'Wrap',
    wrapEnabled: 'Wrap instead of paint',
    embeddedEngine: 'Installed engine',
    editEngine: 'Edit engine',
    updateEngine: 'Update to latest version',
    chooseEngine: 'Choose engine',
    adoptEngine: 'Add to engine library',
    engineOf: 'Engine of {name}',
    engineSummary: '{layout}, {cylinders} cyl. · {liters} l',
    massBreakdown: 'Mass breakdown',
  },
};
